import React, { useMemo, useState, useCallback } from 'react';
import styles from './ProductList.module.css';
import type { ProductRecord } from '../../hooks/useTraining';
import { ProductForm } from './ProductForm';
import type { ProductFormData } from './ProductForm';
import { BulkImportModal, PRODUCT_COLUMNS } from './BulkImportModal';

export interface ProductListProps {
  products: ProductRecord[];
  onCreateProduct?: (data: Record<string, unknown>) => Promise<void>;
  onUpdateProduct?: (id: number, data: Record<string, unknown>) => Promise<void>;
  onDeleteProduct?: (id: number) => Promise<void>;
  onBulkCreate?: (records: Record<string, unknown>[]) => Promise<unknown>;
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  onCreateProduct,
  onUpdateProduct,
  onDeleteProduct,
  onBulkCreate,
}) => {
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingProduct, setEditingProduct] = useState<ProductRecord | null>(null);
  const [showImport, setShowImport] = useState(false);
  const isAdmin = !!(onCreateProduct || onUpdateProduct || onDeleteProduct);

  const grouped = useMemo(() => {
    const groups: Record<string, ProductRecord[]> = {};
    products.forEach((p) => {
      const cat = p.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [products]);

  const sortedCategories = useMemo(() => {
    return Object.keys(grouped).sort();
  }, [grouped]);

  const handleCreate = useCallback(async (data: ProductFormData) => {
    if (!onCreateProduct) return;
    await onCreateProduct({ ...data });
    setModalMode(null);
  }, [onCreateProduct]);

  const handleEdit = useCallback(async (data: ProductFormData) => {
    if (!onUpdateProduct || !editingProduct) return;
    await onUpdateProduct(editingProduct.id, { ...data });
    setModalMode(null);
    setEditingProduct(null);
  }, [onUpdateProduct, editingProduct]);

  const handleDelete = useCallback(async (product: ProductRecord) => {
    if (!onDeleteProduct) return;
    if (!window.confirm(`Delete product "${product.name}"? This cannot be undone.`)) return;
    await onDeleteProduct(product.id);
  }, [onDeleteProduct]);

  const openEdit = useCallback((product: ProductRecord) => {
    setEditingProduct(product);
    setModalMode('edit');
  }, []);

  const formatPrice = (price: number) => {
    return price > 0 ? `$${price.toFixed(2)}` : '--';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Product Catalog</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={styles.count}>{products.length} products</span>
          {isAdmin && (
            <>
              <button className={styles.adminBtn} onClick={() => setModalMode('create')}>
                + New Product
              </button>
              {onBulkCreate && (
                <button className={styles.adminBtn} onClick={() => setShowImport(true)}>
                  Import CSV/XLSX
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showImport && onBulkCreate && (
        <BulkImportModal
          resourceName="Products"
          columns={PRODUCT_COLUMNS}
          onImport={onBulkCreate}
          onClose={() => setShowImport(false)}
        />
      )}

      {modalMode && (
        <div className={styles.modalOverlay} onClick={() => { setModalMode(null); setEditingProduct(null); }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {modalMode === 'edit' ? 'Edit Product' : 'New Product'}
            </h3>
            <ProductForm
              onSubmit={modalMode === 'edit' ? handleEdit : handleCreate}
              onCancel={() => { setModalMode(null); setEditingProduct(null); }}
              initialData={modalMode === 'edit' && editingProduct ? editingProduct : undefined}
            />
          </div>
        </div>
      )}

      <div className={styles.list}>
        {sortedCategories.length === 0 && (
          <div className={styles.emptyState}>No products in catalog yet</div>
        )}

        {sortedCategories.map((category) => (
          <div className={styles.categoryGroup} key={category}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryLabel}>{category}</span>
              <span className={styles.categoryCount}>{grouped[category].length}</span>
            </div>

            {grouped[category].map((product) => (
              <div className={styles.productItem} key={product.id}>
                <div className={styles.productName}>{product.name}</div>

                <div className={styles.productMeta}>
                  {product.brand && (
                    <span className={`${styles.metaTag} ${styles.metaTagBrand}`}>{product.brand}</span>
                  )}
                  {product.sku && (
                    <span className={styles.metaTag}>{product.sku}</span>
                  )}
                  <span className={`${styles.metaTag} ${styles.metaTagPrice}`}>
                    {formatPrice(product.price)}/{product.unit}
                  </span>
                  <span className={`${styles.metaTag} ${product.inStock ? styles.metaTagStock : styles.metaTagOos}`}>
                    {product.inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>

                <div className={styles.productDetails}>
                  {product.vendor && (
                    <span>Vendor: <span className={styles.detailValue}>{product.vendor}</span></span>
                  )}
                  {product.leadTimeDays > 0 && (
                    <span>Lead: <span className={styles.detailValue}>{product.leadTimeDays}d</span></span>
                  )}
                  {product.notes && (
                    <span>{product.notes}</span>
                  )}
                </div>

                {isAdmin && (
                  <div className={styles.adminActions}>
                    <button className={styles.adminBtnSmall} onClick={() => openEdit(product)}>Edit</button>
                    <button className={`${styles.adminBtnSmall} ${styles.adminBtnDanger}`} onClick={() => handleDelete(product)}>Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
